/**
 * Tab Nav Singleton - Picks up click events on the page and opens associated page/body overlays
 */
class GMXTabNavigationSingleton {
	mutationObserver: MutationObserver

	constructor() {
		// @ts-expect-error:  adding global class to window
		if (!window.gmxTabNavigationSingleton) {
			// @ts-expect-error:  adding global class to window
			window.gmxTabNavigationSingleton = this

			this.setupTabNavShowListener()
			this.initTabs(document.body)

			this.mutationObserver = new MutationObserver(this._onDOMMutation.bind(this))

			this.mutationObserver.observe(document.getElementsByTagName('body')[0], {
				childList: true,
				subtree: true
			})
		} else {
			window.console.log('Tried to instantiate tabbed nav singleton...already instantiated')
		}
	}

	/**
	 * Method to setup a click even listener on the body and if it picks up a click for a tab with a tab id (aria-controls)
	 * the do the necessary work to activate the tab and it's associated tab content and its content group
	 * @private
	 */
	private setupTabNavShowListener(root: ShadowRoot | HTMLElement = document.body): void {
		root.addEventListener('gmxTabValueChange', (e: CustomEvent) => {
			const targetTabId = e.detail.selectedItem
			const noScrollTrigger = e.detail.noScrollTrigger || false

			if (!targetTabId) {
				// Tab Nav id not found, look up through ancestors to see if a child element captured the click event
				window.console.warn(`WARN:  Caught a tab value change event but there was no selectedItem in the detail`)

				return
			}

			const targetTabNavContentPane = root.querySelector(`.gmx-tab-content-pane#${targetTabId}`)

			if (targetTabNavContentPane) {
				const parentTabNavContainer = targetTabNavContentPane.closest('.gmx-tab-content-group')

				if (parentTabNavContainer) {
					if (parentTabNavContainer.classList.contains('tabs-as-anchors')) {
						// First find the id associated
						const targetPane = parentTabNavContainer.querySelector(`#${targetTabId}`)

						if (targetPane && !noScrollTrigger) {
							targetPane.scrollIntoView({
								behavior: 'smooth',
							})
						}
					} else {
						const groupTabs = parentTabNavContainer.querySelectorAll('.gmx-tab-content-pane')
						groupTabs.forEach(tab => {
							if (tab.getAttribute('id') === targetTabId) {
								tab.setAttribute('aria-hidden', 'false')
							} else {
								tab.setAttribute('aria-hidden', 'true')
							}
						})
					}
				}
			} else {
					window.console.warn(`WARN: Tab Pane with id ${targetTabId} not found`)
			}
		})
	}

	/**
	 * Method to init the tabs that are children of an element
	 *
	 * @param parentEl {HTMLElement | ShadowRoot} - Element to search for tabs
	 */
	initTabs(parentEl: HTMLElement | ShadowRoot): void {
		const tabEls = parentEl.querySelectorAll('gmx-tabs')

		tabEls.forEach(tabs => {
			const childTabs = tabs.querySelectorAll(':scope > ul > li .tab-link-btn')

			childTabs.forEach(t => {
				if (t.getAttribute('aria-selected') === 'true') {
					const targetTabContentPane = parentEl.querySelector(`.gmx-tab-content-pane#${t.getAttribute('aria-controls')}`)

					if (targetTabContentPane) {
						targetTabContentPane.setAttribute('aria-hidden', 'false')
					}
				}
			})
		})
	}

	/**
	 * Callback for the MutationObserver setup on the body to look for newely added elements and if they have tabs as children
	 * that may need to be initialized
	 *
	 * @param mutations {MutationRecord}
	 * @private
	 */
	private _onDOMMutation(mutations: MutationRecord): void {
		if (Array.isArray(mutations)) {
			mutations.forEach(mut => {
				const target = mut.target
				const addedNode = mut.addedNodes && mut.addedNodes[0]

				if (target && target.nodeType === 1) {
					if (addedNode && addedNode.nodeType === 1) {
						this.initTabs(addedNode)
					}
				}
			})
		}
	}
}

const tabbedNavSingleton = Object.freeze(new GMXTabNavigationSingleton())
export default tabbedNavSingleton
